import {defineStore} from 'pinia'
import EventService from '@/services/APIService.js'
import { twoFactorAuthStore } from '@/store/pinia/twoFactorAuthStore'
import { resetAllSearchStores } from '@/store/pinia/searchStore.js'

// useStore could be anything like useUser, useCart
// the first argument is a unique id of the store across your application
// Session keepalive interval in milliseconds (5 minutes)
const KEEPALIVE_INTERVAL = 5 * 60 * 1000;

// Storage key for cross-tab session sync
const SESSION_SYNC_KEY = 'pling:session_sync';

export const sessionStore = defineStore('PlingSessionStore', {
    state: () => {
        return {
            // all these properties will have their type inferred automatically

            state: 'initializing',
            authenticated: false,
            endpoint: null,
            server: null,
            servers: null,
            is_test_server: false,
            auth_token: null,
            user_data: null,
            license: null,

            shops: null,
            loading_shops: false,

            background_jobs: [],
            errors: null,

            export_jobs: [],
            breadcrumb: [],


            primary_menu: false,
            server_selector: false,
            dark_mode: false,

            app_bar: true,

            locale_presets: [],
            entity_locales: [],
            locale: null,
            client_version: process.env.VUE_APP_GIT_HASH,
            server_version: null,

            price_list_types: null,
            loading_price_list_types: null,

            // Keepalive interval ID
            _keepaliveIntervalId: null,
            // Track if storage listener is set up
            _storageListenerActive: false,

            scanner_mode: null,

        }
    },
    getters: {

        /**
         * Returns all licensed modules that are active
         */
        licensedModules(state) {
            if (!state.license || !state.license.modules) {
                return [];
            }

            return Object.keys(state.license.modules).filter(key => {
                return state.license.modules[key].is_active === true;
            });
        },

        getUserRoles(state) {

            if (state.user_data == null) {
                return [];
            }
            return state.user_data.roles_inherited;

        },

        isAdmin(state) {


            if (state.user_data == null) {
                return false;
            }

            if (state.user_data.roles_inherited.indexOf('admin') !== -1) {

                return true;

            }


            return false;

        }

    },

    actions: {

        /**
         * Returns the highest customer right the current user has for the given shop IDs.
         * Returns null if user has no shop restrictions (superadmin).
         * Returns 'edit' if any matching shop has edit or inherit.
         * Returns 'view' if any matching shop has view.
         * Returns 'deny' if all matching shops explicitly deny.
         *
         * @param {string[]} shopSlugs - slugs from customer.data.shops
         * @returns {'edit'|'view'|'deny'|null}
         */
        getCustomerRight(shopSlugs) {
            const permissions = this.user_data?.shop_permissions;

            if (!permissions?.length) {
                return null; // superadmin — no restrictions
            }

            const relevant = permissions.filter(p => p.customer && shopSlugs.includes(p.shop_slug));

            if (!relevant.length) {
                return null; // no allocation to any of these shops
            }

            const rights = relevant.map(p => p.customer);

            if (rights.some(r => r === 'edit' || r === 'inherit')) return 'edit';
            if (rights.some(r => r === 'view')) return 'view';
            return 'deny';
        },

        /**
         * Whether the current user may create orders in the given shop.
         * Mirrors the server-side USER_PERMISSION_EDIT_ORDER voter: users with no
         * shop restrictions (superadmin) may create everywhere; restricted users
         * need an explicit 'edit'/'inherit' order right. This excludes shops the
         * user only sees on a need-to-know basis ('fulfilment'), as well as
         * 'view'/'deny'/unallocated shops, which the create endpoint would reject.
         *
         * @param {string} shopSlug
         * @returns {boolean}
         */
        canCreateOrderInShop(shopSlug) {
            const permissions = this.user_data?.shop_permissions;

            if (!permissions?.length) {
                return true; // superadmin — no restrictions
            }

            const entry = permissions.find(p => p.shop_slug === shopSlug);

            if (!entry) {
                return false; // restricted user with no allocation to this shop
            }

            return entry.order === 'edit' || entry.order === 'inherit';
        },

        hasModuleLicense(module) {

            // No license loaded yet
            if (!this.license || !this.license.modules) {
                return false;
            }

            // Module doesn't exist in license
            if (!this.license.modules[module]) {
                return false;
            }

            // Check if module is active
            return this.license.modules[module].is_active === true;

        },

        getModuleInfo(module) {

            // No license loaded yet
            if (!this.license || !this.license.modules) {
                return null;
            }

            // Return module info or null
            return this.license.modules[module] || null;

        },

        hasUserRole(role) {


            let roles = this.getUserRoles;
            return roles.indexOf(role) !== -1;

        },

        setScannerMode(mode) {
            this.scanner_mode = (mode === 'storage' || mode === 'pos') ? mode : null;
            if (this.scanner_mode) {
                localStorage.setItem('pling:scanner_mode', this.scanner_mode);
            } else {
                localStorage.removeItem('pling:scanner_mode');
            }
        },

        changeLocale(locale) {

            this.locale = locale;
            localStorage.setItem('pling:locale', locale);

            EventService.defaults.headers.common['Override-Locale'] = locale;

            // Dayjs locale is already set to 'de' by default in the plugin
            // Additional locales can be imported dynamically if needed

        },

        async init() {

            this.server = localStorage.getItem('pling:server');
            this.servers = JSON.parse(localStorage.getItem('pling:servers'));

            if (this.servers === null) {
                this.servers = [];
            }

            this.locale = localStorage.getItem('pling:locale');
            if (this.locale === null) {
                this.locale = 'de_DE';
            }

            this.changeLocale(this.locale);

            this.locale_presets = JSON.parse(localStorage.getItem('pling:locale_presets'));
            if (this.locale_presets === null) {
                this.locale_presets = [];
            }

            this.dark_mode = localStorage.getItem('pling:darkmode');
            if (this.dark_mode === 'true') {
                this.dark_mode = true;
            } else {
                this.dark_mode = false;
            }

            const storedScannerMode = localStorage.getItem('pling:scanner_mode');
            this.scanner_mode = (storedScannerMode === 'storage' || storedScannerMode === 'pos') ? storedScannerMode : null;

            let primary_menu = localStorage.getItem('pling:primary_nav_open');
            if (primary_menu == 'true' || primary_menu == null) {
                this.primary_menu = true;
            }

            let server_selector = localStorage.getItem('pling:show_server_selector');
            if (server_selector == 'true') {
                this.server_selector = true;
            }

            this.setServer(this.server);

            // Setup cross-tab session synchronization
            this.setupStorageListener();

            if (this.server) {
                try {
                    await this.getUserData();
                    await this.loadLicense();
                    this.startKeepalive();
                    this.broadcastSessionChange('login');
                    this.state = 'authenticated';
                } catch (e) {
                    this.state = 'ready';
                }
            } else {
                this.state = 'ready';
            }

        },

        ping(silent = false) {

            let self = this;

            if (this.endpoint === null) {

                self.state = 'ready';
                return;

            }

            // Use skipInterceptor for silent pings to avoid triggering logout on network errors
            const config = silent ? {skipInterceptor: true} : {};

            EventService.get('/auth/ping', config).then(function (result) {

                self.is_test_server = result.data.is_test_server;
                self.server_version = result.data.api_version;

                if (result.data.payload !== true) {
                    // Session expired on server side
                    self.stopKeepalive();
                    // Only broadcast if we were previously authenticated
                    const wasAuthenticated = self.state === 'authenticated';
                    self.state = 'ready';
                    // Notify other tabs only if we were logged in
                    if (wasAuthenticated) {
                        self.broadcastSessionChange('expired');
                    }
                } else {

                    self.getUserData();


                    if (self.state !== 'authenticated') {

                        self.loadLicense();
                        self.state = 'authenticated';
                        self.loadPriceListTypes();
                        // Start keepalive after successful authentication
                        self.startKeepalive();

                    }


                }

            }).catch(function (error) {
                // Only handle non-silent pings or actual auth errors
                if (!silent) {
                    self.state = 'ready';
                }
                // If it's a 401, the interceptor will handle logout
            })


        },

        // Start periodic keepalive pings
        startKeepalive() {
            let self = this;

            // Clear any existing interval
            self.stopKeepalive();

            // Start new keepalive interval
            self._keepaliveIntervalId = setInterval(function () {
                if (self.state === 'authenticated') {
                    self.ping(true); // Silent ping
                }
            }, KEEPALIVE_INTERVAL);
        },

        // Stop keepalive pings
        stopKeepalive() {
            if (this._keepaliveIntervalId) {
                clearInterval(this._keepaliveIntervalId);
                this._keepaliveIntervalId = null;
            }
        },

        // Setup cross-tab session synchronization
        setupStorageListener() {
            let self = this;

            if (self._storageListenerActive) return;

            window.addEventListener('storage', function (event) {
                if (event.key === SESSION_SYNC_KEY && event.newValue) {
                    try {
                        const data = JSON.parse(event.newValue);

                        if (data.action === 'logout' || data.action === 'expired') {
                            // Another tab logged out or session expired
                            // Only react if we're currently authenticated to prevent infinite loops
                            if (self.state === 'authenticated') {
                                self.stopKeepalive();
                                sessionStorage.clear();
                                self.$reset();
                                self.init();
                            }
                        } else if (data.action === 'login') {
                            // Another tab logged in - update this tab's state
                            self.ping();
                        }
                    } catch (e) {
                        console.error('Error parsing session sync data:', e);
                    }
                }

                // A new tab is asking for a token — hand off ours if we have one
            });

            self._storageListenerActive = true;
        },

        // Broadcast session changes to other tabs
        broadcastSessionChange(action) {
            const data = { action, timestamp: Date.now() };
            localStorage.setItem(SESSION_SYNC_KEY, JSON.stringify(data));
            localStorage.removeItem(SESSION_SYNC_KEY);
        },

        toggleServerSelector() {

            let self = this;
            self.server_selector = !self.server_selector;
            localStorage.setItem('pling:show_server_selector', self.server_selector);


        },

        togglePrimaryMenu(state) {


            let self = this;


            if (state === undefined) {

                self.primary_menu = !self.primary_menu;


            } else {
                self.primary_menu = state;
            }


            localStorage.setItem('pling:primary_nav_open', self.primary_menu);


        },

        setServer(server) {


            var regexp = /(http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-\/]))?/;


            if (server == null) {

                this.endpoint = null;
                return;

            }


            if (process.env.VUE_APP_FORCED_SERVER) {

                this.server = process.env.VUE_APP_FORCED_SERVER;
                this.endpoint = server;

            } else if (process.env.VUE_APP_SERVER_DOMAIN && !server.includes('.')) {

                this.server = server;
                this.endpoint = 'https://' + server + '.' + process.env.VUE_APP_SERVER_DOMAIN;

            } else {

                this.server = server;

                if (!regexp.test(server)) {
                    this.endpoint = 'https://' + server;
                } else {
                    this.endpoint = server;
                }

            }


            localStorage.setItem('pling:server', this.server);
            EventService.defaults.baseURL = this.endpoint;

        },

        addServer(server) {


            let self = this;

            let index = self.servers.indexOf(server);

            if (index === -1) {

                self.servers.push(server);

                localStorage.setItem('pling:servers', JSON.stringify(self.servers));

            }

        },

        removeServer(server) {
            this.servers = this.servers.filter(s => s !== server);
            localStorage.setItem('pling:servers', JSON.stringify(this.servers));
            if (this.server === server) {
                this.server = null;
                this.endpoint = null;
                localStorage.removeItem('pling:server');
            }


        },


        loadPriceListTypes() {


            let self = this;


            EventService.get('/api/v3/price-lists/types').then((response) => {

                self.price_list_types = response.data.payload;

            });


        },

        async loadLicense() {
            let self = this;
            return EventService.get('/api/v3/license').then((response) => {

                self.license = response.data.payload;

            });


        },

        async getUserData() {

            let self = this;

            const response = await EventService.post('/api/v3/user/me', {expand: ["shops", "roles", "sessions", "api_keys", "ip_whitelist", "channels", "shop_permissions"]});
            self.user_data = response.data.payload;

            if (self.user_data?.avatar && self.server && self.user_data?.login) {
                localStorage.setItem(`pling:gravatar:${self.server}:${self.user_data.login}`, self.user_data.avatar);
                localStorage.setItem(`pling:display_name:${self.server}:${self.user_data.login}`, self.user_data.display_name || self.user_data.login);
            }

        },

        getBackgroundJobs() {

            let self = this;

            let params = {
                "object": "export-job",
                "scope": [
                    "results"
                ],

                params: {
                    "query": {
                        "search_input": "",
                        "user": [
                            self.user_data.id
                        ],
                        "user_search_method": null
                    },
                    page_size: 100

                }

            };


            EventService.post('/api/v3/search', params).then((response) => {

                self.export_jobs = response.data.payload.results;

            });

        },

        async loadShops() {

            let self = this;

            self.loading_shops = true;

            return EventService.post('/api/v3/user/me', {expand: ["shops", "price_lists"]}).then((response) => {

                self.shops = response.data.payload.shops;
                self.loading_shops = false;

            });

        },

        async getEntityLocales() {


            let self = this;

            return EventService.get('api/erp/v2/info/locales').then((result) => {

                self.entity_locales = result.data.payload;

            });


        },

        setLocalePreset(type, preset) {


            let self = this;


            let index = self.locale_presets.findIndex(pr => pr.type === type);

            if (index === -1) {
                self.locale_presets.push({

                    type: type,
                    preset: preset

                });

            } else {


                self.locale_presets[index] = {

                    type: type,
                    preset: preset

                }

            }


            localStorage.setItem('pling:locale_presets', JSON.stringify(self.locale_presets));


        },

        getLocalePreset(type) {

            let self = this;

            let preset = self.locale_presets.find(pr => pr.type === type);


            if (!preset && type !== 'any') {

                return self.getLocalePreset('any');

            }

            return preset.preset;

        },


        signOut(broadcast = true) {

            let self = this;

            // Stop keepalive immediately
            self.stopKeepalive();

            self.state = 'deauthenticating';

            // Notify other tabs before cleanup
            if (broadcast) {
                self.broadcastSessionChange('logout');
            }

            // Helper to clean up state without triggering ping
            const cleanupState = () => {
                sessionStorage.clear();

                // Clear all search-related localStorage entries
                const keysToRemove = [];
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    if (key && key.startsWith('search::')) {
                        keysToRemove.push(key);
                    }
                }
                keysToRemove.forEach(key => localStorage.removeItem(key));

                // Reset all search store instances in memory
                resetAllSearchStores();

                self.$reset();

                // Restore essential state without calling ping
                self.server = localStorage.getItem('pling:server');
                self.servers = JSON.parse(localStorage.getItem('pling:servers')) || [];
                self.locale = localStorage.getItem('pling:locale') || 'de_DE';
                self.dark_mode = localStorage.getItem('pling:darkmode') === 'true';
                self.locale_presets = JSON.parse(localStorage.getItem('pling:locale_presets')) || [];

                let primary_menu = localStorage.getItem('pling:primary_nav_open');
                if (primary_menu == 'true' || primary_menu == null) {
                    self.primary_menu = true;
                }

                let server_selector = localStorage.getItem('pling:show_server_selector');
                if (server_selector == 'true') {
                    self.server_selector = true;
                }

                const storedScannerMode = localStorage.getItem('pling:scanner_mode');
                self.scanner_mode = (storedScannerMode === 'storage' || storedScannerMode === 'pos') ? storedScannerMode : null;

                self.setServer(self.server);
                self.setupStorageListener();

                // Set state to ready - show login screen
                self.state = 'ready';
            };

            EventService.post('/auth/logout', {}, {
                skipInterceptor: true,
            }).then((result) => {
                cleanupState();
            }).catch((error) => {
                // Even if logout request fails, clear local state
                cleanupState();
            });


        },

        async signIn(credentials) {

            let self = this;

            self.setServer(credentials.server);

            if (self.endpoint === null) {
                return false;
            }

            self.state = 'authenticating'
            EventService.defaults.baseURL = this.endpoint;

            localStorage.setItem('pling.credentials.last_login', credentials.login);

            return EventService.post('/auth/login', credentials, {
                suppressToast: true,
            }).then(async function (response) {

                const payload = response.data.payload;

                if (!payload) {
                    self.state = 'ready';
                    return { loginError: response.data.error || response.data.message || null };
                }

                const twoFactorAuth = twoFactorAuthStore()
                const methods = Array.isArray(payload.methods) ? payload.methods : Object.values(payload.methods);

                // Handle 2FA registration requirement
                if (payload.is_requires_2fa_register && (methods.length > 0)) {
                    twoFactorAuth.all2FAMethods = methods;
                    twoFactorAuth.available2FAMethods = methods;
                    self.state = '2fa_method_registration_start';

                    if (payload.is_requires_2fa_auth) {
                        twoFactorAuth.canDismiss = false;
                        return;
                    }

                    // Can dismiss — load session normally, show registration popup
                    self.addServer(self.server);
                    await self.getUserData();
                    self.broadcastSessionChange('login');
                    return;
                }

                // Handle 2FA verification requirement
                if (payload.is_requires_2fa_auth) {
                    twoFactorAuth.all2FAMethods = methods;
                    twoFactorAuth.available2FAMethods = methods;
                    self.addServer(self.server);
                    self.state = 'awaiting_2fa';
                    return;
                }

                // Normal login — cookie is now set, no need to store API key
                self.addServer(self.server);

                await Promise.all([
                    self.getUserData(),
                    self.loadLicense()
                ]);

                self.state = 'authenticated';
                self.startKeepalive();
                self.broadcastSessionChange('login');

            }).catch(function (error) {
                self.state = 'ready';
            });
        },

        toggleDarkMode() {


            this.dark_mode = !this.dark_mode;
            localStorage.setItem('pling:darkmode', this.dark_mode);


        },

        async processSessionAuthentication() {

            await this.getUserData();
            await this.loadLicense();
            this.startKeepalive();
            this.broadcastSessionChange('login');
            this.state = 'authenticated';

        }

    },


})
